import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Bell, Pin } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { IconButton } from '@/components/ui/Button'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { Skeleton } from '@/components/ui/Skeleton'
import { useAuth } from '@/features/auth/useAuth'
import { cn } from '@/utils/cn'
import { feedQuery, unreadCountQuery, useMarkRead } from '../api'
import { noticesHref, relativeTime } from '../meta'
import { CategoryTile } from './CategoryChip'

/** Topbar bell: unread badge (polled every minute) and the five latest notices. */
export function NotificationsPopover() {
  const { t } = useTranslation('announcements')
  const { status, portal, can } = useAuth()
  const [open, setOpen] = useState(false)
  const enabled = status === 'authenticated' && can('announcements.view')
  const unread = useQuery(unreadCountQuery(enabled))
  const latest = useQuery({ ...feedQuery({ page: 1, perPage: 5 }), enabled: enabled && open })
  const markRead = useMarkRead()
  const count = unread.data ?? 0
  const href = noticesHref(portal, can('announcements.manage'))
  const label = count > 0 ? t('Notifications, {{count}} unread', { count }) : t('Notifications')
  // No bell when the school does not use announcements (or the user may not read them).
  if (status === 'authenticated' && !enabled) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IconButton
          label={label}
          className="relative"
          icon={
            <>
              <Bell />
              {count > 0 ? (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute top-0.5 right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-danger-600 px-1 text-[0.625rem] leading-none font-semibold text-white ring-2 ring-surface"
                >
                  {count > 99 ? '99+' : count}
                </span>
              ) : null}
            </>
          }
        />
      </PopoverTrigger>
      <PopoverContent className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <p className="text-sm font-semibold text-ink">{t('Notifications')}</p>
          {count > 0 ? (
            <span className="text-xs font-medium text-brand-700">{t('{{count}} unread', { count })}</span>
          ) : null}
        </div>
        {!enabled ? (
          <p className="px-4 py-8 text-center text-sm text-muted">{t('No notifications.')}</p>
        ) : latest.isPending ? (
          <ul className="space-y-3 p-4" aria-label={t('Loading notifications')}>
            {Array.from({ length: 3 }, (_, i) => (
              <li key={i} className="flex gap-3">
                <Skeleton className="size-9 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </li>
            ))}
          </ul>
        ) : latest.error ? (
          <p className="px-4 py-8 text-center text-sm text-muted">{t('Could not load notifications.')}</p>
        ) : latest.data.data.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell className="mx-auto size-5 text-subtle" aria-hidden="true" />
            <p className="mt-2 text-sm text-muted">{t("You're all caught up.")}</p>
          </div>
        ) : (
          <ul className="max-h-96 divide-y divide-line overflow-y-auto">
            {latest.data.data.map((item) => (
              <li key={item.id}>
                <PopoverClose asChild>
                  <Link
                    to={href}
                    onClick={() => {
                      if (!item.isRead) markRead.mutate(item.id)
                    }}
                    className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-surface-muted focus-visible:bg-surface-muted"
                  >
                    <CategoryTile category={item.category} className="size-9 [&_svg]:size-4" />
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          'line-clamp-2 text-sm text-ink',
                          item.isRead ? 'font-normal' : 'font-semibold',
                        )}
                      >
                        {item.isPinned ? (
                          <Pin
                            className="mr-1 inline size-3 align-[-1px] text-brand-600"
                            aria-label={t('Pinned')}
                          />
                        ) : null}
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-xs text-subtle">
                        {relativeTime(item.publishedAt)} · {item.authorName}
                      </p>
                    </div>
                    {!item.isRead ? (
                      <>
                        <span
                          className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-600"
                          aria-hidden="true"
                        />
                        <span className="sr-only">{t('Unread')}</span>
                      </>
                    ) : null}
                  </Link>
                </PopoverClose>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-line p-2">
          <PopoverClose asChild>
            <Link
              to={href}
              className="flex items-center justify-center gap-1.5 rounded-control px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
            >
              {t('View all')} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </PopoverClose>
        </div>
      </PopoverContent>
    </Popover>
  )
}
